import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { individualAPI } from '../services/api';

// Fetches the signed-in student's demo / paid-validity state.
//
// Owned by Layout and passed down, so the countdown strip and the expired
// screen share one request instead of each polling separately.
//
// The state comes from the server on every load rather than from the JWT: the
// token is issued once at login and would still claim "30 days left" a month
// later. The same computation backs the middleware that blocks the requests,
// so what the student is shown and what the server will serve stay in step.
export default function useIndividualAccess() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (user?.role !== 'student') {
      setAccess(null);
      setLoading(false);
      return;
    }
    individualAPI.getMyAccess()
      .then((res) => setAccess(res.data.data))
      // A failed check must not lock a paying student out of the UI. The
      // server is still the boundary — it refuses the actual content
      // regardless of what this call returned.
      .catch(() => setAccess(null))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // A student can leave the tab open past midnight, at which point the
  // countdown would keep showing yesterday's number. Re-checking hourly, and
  // whenever the tab comes back to the foreground, keeps it honest without
  // polling aggressively.
  useEffect(() => {
    if (user?.role !== 'student') return undefined;
    const timer = setInterval(load, 60 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, load]);

  return { access, loading, reload: load };
}
